import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Select from "react-select";
import { DatePicker } from "antd";
import moment from "moment";
import axios from "axios";

import { useAtom } from "jotai";
import { gbState, fYear } from "../../../components/shared/GlobalState";
import { FaSave, FaArrowLeft } from "react-icons/fa";

const AssetOutwardChild = ({ issued }) => {
  const navigate = useNavigate();
  const [issuedAsset, setIssuedAsset] = useState(issued);
  const [financialYear] = useAtom(fYear);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    asset_id: issuedAsset?.asset_id,
    item_code: issuedAsset?.item_code,
    quantity: null,
    issued_rate: null,
    issued_description: null,
    issue_type: null,
    person: null,
    mobile: null,
    expected_date_of_return: null,
    comments: null,
  });

  const outwardOptions = [
    { value: 1, label: "Sale" },
    { value: 2, label: "Donated" },
    { value: 3, label: "Write Off" },
    { value: 4, label: "Transferred" },
  ];

  //Error Logging Service & Santize Input
  const [{ errorLogApi, user }] = useAtom(gbState);
  
  const validateForm = () => {
    const newErrors = {};
    if (!formData.issue_type) {
      newErrors.issue_type = "Please select issue type";
    }
    if (!formData.quantity) {
      newErrors.quantity = "Quantity is required";
    } else if (issuedAsset?.closing_stock < formData.quantity) {
      newErrors.quantity = `Cannot issue more than ${issuedAsset?.closing_stock}`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      border: state.isFocused ? "1px solid #3b82f6" : "1px solid #d1d5db",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.25)" : "none",
      borderRadius: "0.375rem",
      padding: "2px",
      fontSize: "0.875rem",
      transition: "all 0.2s",
      "&:hover": {
        borderColor: state.isFocused ? "#3b82f6" : "#9ca3af",
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      borderRadius: "0.375rem",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#dbeafe" : "white",
      color: state.isSelected ? "white" : "#374151",
      fontSize: "0.875rem",
      padding: "8px 12px",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#9ca3af",
    }),
  };

  const handleChange = ({ currentTarget: input }) => {
    // Clear error when user starts typing
    if (errors[input.name]) {
      setErrors({
        ...errors,
        [input.name]: null
      });
    }
    
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };
  
  // Common styles
  const inputStyle = `w-full p-3 font-sans text-sm border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`;
  const buttonBaseStyle = `flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md transition-all duration-200 font-medium`;
  
  const dropDownChange = (selected, dropdown) => {
    // Clear error when user selects an option
    if (errors[dropdown.name]) {
      setErrors({
        ...errors,
        [dropdown.name]: null
      });
    }
    
    setFormData({
      ...formData,
      [dropdown.name]: selected.value,
    });
  };

  const handleAssetIssuance = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const userToken = localStorage.getItem("token");
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/church/outward/add`,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        data: { ...formData, financial_year: financialYear?.label },
      });
      
      toast.success("Asset issued successfully!");
      navigate("/assets/inventory");
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to issue asset";
      toast.error(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage);
      errorLogApi(error, "AssetOutwardChild", "/church/outward/add");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col w-full bg-gray-50 min-h-screen">
      <div className="p-6 w-full max-w-4xl mx-auto">
        {/* Header with title and actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Issue Asset</h1>
          
          <div className="flex gap-3">
            <button
              className={`${buttonBaseStyle} bg-gray-600 hover:bg-gray-700`}
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
            >
              <FaArrowLeft size={16} />
              <span>Back</span>
            </button>
            
            <button
              className={`${buttonBaseStyle} ${isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              onClick={handleAssetIssuance}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FaSave size={16} />
                  <span>Save Outward</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <form onSubmit={(e) => { e.preventDefault(); handleAssetIssuance(); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Item/Asset Name
                  </label>
                  <input
                    type="text"
                    name="asset_name"
                    value={issued?.asset_name || ""}
                    disabled
                    className={`${inputStyle} bg-gray-100 cursor-not-allowed`}
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Item Code
                  </label>
                  <input
                    type="text"
                    name="item_code"
                    value={formData?.item_code || ""}
                    disabled
                    className={`${inputStyle} bg-gray-100 cursor-not-allowed`}
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Available Quantity
                  </label>
                  <input
                    type="text"
                    name="available_quantity"
                    value={issuedAsset?.closing_stock || ""}
                    disabled
                    className={`${inputStyle} bg-gray-100 cursor-not-allowed`}
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Issue Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    name="issue_type"
                    onChange={(selected, dropdown) => {
                      // Clear error when user selects an option
                      if (errors.issue_type) {
                        setErrors({
                          ...errors,
                          issue_type: null
                        });
                      }
                      setFormData({
                        ...formData,
                        [dropdown.name]: selected.value,
                      });
                    }}
                    options={outwardOptions}
                    placeholder="Select Issue Type"
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    className={errors.issue_type ? 'border-red-500 ring-1 ring-red-500' : ''}
                  />
                  {errors.issue_type && (
                    <p className="mt-1 text-sm text-red-500">{errors.issue_type}</p>
                  )}
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Outward Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity || ""}
                    onChange={handleChange}
                    placeholder="Enter quantity"
                    className={`${inputStyle} ${errors.quantity ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                  />
                  {errors.quantity && (
                    <p className="mt-1 text-sm text-red-500">{errors.quantity}</p>
                  )}
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Issued Rate
                  </label>
                  <input
                    type="number"
                    name="issued_rate"
                    value={formData.issued_rate || ""}
                    onChange={handleChange}
                    placeholder="Enter rate"
                    className={inputStyle}
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Person Name
                  </label>
                  <input
                    type="text"
                    name="person"
                    value={formData.person || ""}
                    onChange={handleChange}
                    placeholder="Enter person name"
                    className={inputStyle}
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Mobile
                  </label>
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile || ""}
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                    className={inputStyle}
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Expected Date of Return
                  </label>
                  <DatePicker
                    className={`${inputStyle} h-[42px]`}
                    name="expected_date_of_return"
                    onChange={(date, dateString) => {
                      setFormData({
                        ...formData,
                        expected_date_of_return: dateString,
                      });
                    }}
                    disabledDate={(current) => {
                      let customDate = moment().format("YYYY-MM-DD");
                      return (
                        current && current < moment(customDate, "YYYY-MM-DD")
                      );
                    }}
                    showToday={true}
                    placeholder="Select expected return date"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="issued_description"
                    value={formData.issued_description || ""}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Enter description (optional)"
                    className={inputStyle}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
        
        {/* Helpful tips */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Tips:</h3>
          <ul className="text-sm text-blue-700 list-disc pl-5 space-y-1">
            <li>Select the issue type (Sale, Donated, Write Off, or Transferred)</li>
            <li>Specify the quantity being issued (cannot exceed available quantity)</li>
            <li>For temporary issues, include person details and expected return date</li>
            <li>Add a description for record-keeping purposes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AssetOutwardChild;
